import React from "react";

/* ================================================================
   Vehicle Animations — 8 animated templates
   Car, truck, motorcycle, bus, rocket, spaceship, yacht, bicycle
   2D side-view style with motion animations
   ================================================================ */

// ── 1. Car Driving ───────────────────────────────────────────────────
export const CarDriving: React.FC<Record<string, unknown>> = (props) => {
  const rotation = (props.rotation as number) ?? 0;
  const wheelSpin = rotation * 3;
  return (
    <svg viewBox="0 0 800 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Road */}
      <rect x="0" y="300" width="800" height="100" fill="#374151" />
      <line x1="0" y1="345" x2="800" y2="345" stroke="#fbbf24" strokeWidth="3" strokeDasharray="30 20">
        <animate attributeName="stroke-dashoffset" values="0;-50" dur="1s" repeatCount="indefinite" />
      </line>
      {/* Car body */}
      <g>
        <rect x="250" y="230" width="300" height="70" rx="10" fill="#3b82f6" />
        <path d="M310,230 Q330,170 400,170 Q470,170 490,230" fill="#60a5fa" stroke="#3b82f6" strokeWidth="2" />
        {/* Windows */}
        <path d="M320,230 Q335,185 395,185 L395,230 Z" fill="#bfdbfe" opacity="0.7" />
        <path d="M405,185 Q455,185 475,230 L405,230 Z" fill="#bfdbfe" opacity="0.7" />
        {/* Headlight */}
        <rect x="540" y="250" width="15" height="15" rx="3" fill="#fbbf24" />
        {/* Taillight */}
        <rect x="248" y="255" width="10" height="10" rx="2" fill="#ef4444" />
        {/* Wheels */}
        <g transform={`rotate(${wheelSpin},320,300)`}>
          <circle cx="320" cy="300" r="25" fill="#374151" stroke="#1e293b" strokeWidth="3" />
          <circle cx="320" cy="300" r="10" fill="#6b7280" />
          <line x1="320" y1="275" x2="320" y2="325" stroke="#6b7280" strokeWidth="2" />
          <line x1="295" y1="300" x2="345" y2="300" stroke="#6b7280" strokeWidth="2" />
        </g>
        <g transform={`rotate(${wheelSpin},480,300)`}>
          <circle cx="480" cy="300" r="25" fill="#374151" stroke="#1e293b" strokeWidth="3" />
          <circle cx="480" cy="300" r="10" fill="#6b7280" />
          <line x1="480" y1="275" x2="480" y2="325" stroke="#6b7280" strokeWidth="2" />
          <line x1="455" y1="300" x2="505" y2="300" stroke="#6b7280" strokeWidth="2" />
        </g>
      </g>
      {/* Speed lines */}
      {[240, 260, 280].map((y, i) => (
        <line key={i} x1="180" y1={y} x2={220 - i * 10} y2={y} stroke="#94a3b8" strokeWidth="2" opacity="0.4">
          <animate attributeName="x1" values="180;140;180" dur={`${0.6 + i * 0.2}s`} repeatCount="indefinite" />
        </line>
      ))}
    </svg>
  );
};

// ── 2. Truck Hauling ─────────────────────────────────────────────────
export const TruckHauling: React.FC<Record<string, unknown>> = (props) => {
  const rotation = (props.rotation as number) ?? 0;
  const wheelSpin = rotation * 2;
  return (
    <svg viewBox="0 0 800 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Road */}
      <rect x="0" y="310" width="800" height="90" fill="#374151" />
      <line x1="0" y1="350" x2="800" y2="350" stroke="#fbbf24" strokeWidth="3" strokeDasharray="30 20">
        <animate attributeName="stroke-dashoffset" values="0;-50" dur="1.5s" repeatCount="indefinite" />
      </line>
      {/* Trailer */}
      <rect x="100" y="180" width="300" height="130" rx="5" fill="#ef4444" stroke="#dc2626" strokeWidth="2" />
      <text x="250" y="255" textAnchor="middle" fill="#fff" fontSize="20" fontWeight="bold">CARGO</text>
      {/* Cab */}
      <rect x="400" y="210" width="120" height="100" rx="5" fill="#1e40af" stroke="#1e3a8a" strokeWidth="2" />
      <rect x="430" y="220" width="60" height="40" rx="3" fill="#bfdbfe" opacity="0.7" />
      <rect x="510" y="240" width="12" height="20" rx="2" fill="#fbbf24" />
      {/* Wheels */}
      {[180, 330, 460].map((wx, i) => (
        <g key={i} transform={`rotate(${wheelSpin},${wx},310)`}>
          <circle cx={wx} cy="310" r="22" fill="#374151" stroke="#1e293b" strokeWidth="3" />
          <circle cx={wx} cy="310" r="8" fill="#6b7280" />
          <line x1={wx} y1={288} x2={wx} y2={332} stroke="#6b7280" strokeWidth="2" />
        </g>
      ))}
      {/* Exhaust */}
      <circle cx="100" cy="300" r="5" fill="#6b7280" opacity="0.3">
        <animate attributeName="cx" values="100;60;20" dur="2s" repeatCount="indefinite" />
        <animate attributeName="r" values="5;12;20" dur="2s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.3;0.15;0" dur="2s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
};

// ── 3. Motorcycle Lean ───────────────────────────────────────────────
export const MotorcycleLean: React.FC<Record<string, unknown>> = (props) => {
  const rotation = (props.rotation as number) ?? 0;
  const lean = Math.sin(rotation * 0.02) * 5;
  const wheelSpin = rotation * 4;
  return (
    <svg viewBox="0 0 800 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Road */}
      <rect x="0" y="310" width="800" height="90" fill="#374151" />
      <line x1="0" y1="350" x2="800" y2="350" stroke="#fbbf24" strokeWidth="2" strokeDasharray="30 20">
        <animate attributeName="stroke-dashoffset" values="0;-50" dur="0.5s" repeatCount="indefinite" />
      </line>
      <g transform={`rotate(${lean},400,300)`}>
        {/* Frame */}
        <line x1="330" y1="280" x2="470" y2="280" stroke="#1e293b" strokeWidth="6" />
        <line x1="330" y1="280" x2="300" y2="310" stroke="#1e293b" strokeWidth="4" />
        <line x1="470" y1="280" x2="500" y2="310" stroke="#1e293b" strokeWidth="4" />
        {/* Handlebar */}
        <line x1="460" y1="280" x2="480" y2="250" stroke="#475569" strokeWidth="4" />
        <line x1="470" y1="255" x2="490" y2="260" stroke="#475569" strokeWidth="3" />
        {/* Seat */}
        <rect x="360" y="265" width="60" height="15" rx="7" fill="#1e293b" />
        {/* Engine */}
        <rect x="370" y="280" width="40" height="20" rx="3" fill="#dc2626" />
        {/* Rider */}
        <circle cx="390" cy="240" r="14" fill="#fbbf24" />
        <rect x="382" y="254" width="16" height="15" rx="3" fill="#1e293b" />
        {/* Wheels */}
        <g transform={`rotate(${wheelSpin},300,310)`}>
          <circle cx="300" cy="310" r="22" fill="none" stroke="#374151" strokeWidth="5" />
          <circle cx="300" cy="310" r="6" fill="#6b7280" />
        </g>
        <g transform={`rotate(${wheelSpin},500,310)`}>
          <circle cx="500" cy="310" r="22" fill="none" stroke="#374151" strokeWidth="5" />
          <circle cx="500" cy="310" r="6" fill="#6b7280" />
        </g>
        {/* Headlight */}
        <circle cx="518" cy="290" r="6" fill="#fbbf24" />
      </g>
    </svg>
  );
};

// ── 4. Bus Route ─────────────────────────────────────────────────────
export const BusRoute: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const stops = ["Home", "School", "Park", "Market", "Library"];
  const stopX = [100, 220, 370, 520, 670];
  const busX = stopX[Math.min(activeIndex, stops.length - 1)];
  return (
    <svg viewBox="0 0 800 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Road */}
      <rect x="50" y="250" width="700" height="30" rx="5" fill="#374151" />
      <line x1="50" y1="265" x2="750" y2="265" stroke="#fbbf24" strokeWidth="2" strokeDasharray="10 8" />
      {/* Stops */}
      {stops.map((s, i) => (
        <g key={i}>
          <rect x={stopX[i] - 3} y="220" width="6" height="30" fill="#6b7280" />
          <rect x={stopX[i] - 15} y="212" width="30" height="12" rx="3" fill={i <= activeIndex ? "#22c55e" : "#334155"} />
          <text x={stopX[i]} y="305" textAnchor="middle" fill={i <= activeIndex ? "#e0e7ff" : "#475569"} fontSize="11">{s}</text>
        </g>
      ))}
      {/* Route line */}
      <line x1="100" y1="218" x2="670" y2="218" stroke="#22c55e" strokeWidth="2" opacity="0.3" />
      {/* Bus */}
      <g>
        <rect x={busX - 40} y="160" width="80" height="55" rx="6" fill="#f59e0b" stroke="#d97706" strokeWidth="2">
          <animate attributeName="y" values="162;158;162" dur="0.6s" repeatCount="indefinite" />
        </rect>
        <rect x={busX - 30} y="168" width="20" height="18" rx="2" fill="#bfdbfe" opacity="0.8" />
        <rect x={busX + 5} y="168" width="20" height="18" rx="2" fill="#bfdbfe" opacity="0.8" />
        <text x={busX} y="208" textAnchor="middle" fill="#92400e" fontSize="8" fontWeight="bold">BUS</text>
        <circle cx={busX - 25} cy="218" r="8" fill="#374151" stroke="#1e293b" strokeWidth="2" />
        <circle cx={busX + 25} cy="218" r="8" fill="#374151" stroke="#1e293b" strokeWidth="2" />
      </g>
      <text x="400" y="360" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Stop {activeIndex + 1}: {stops[activeIndex]}</text>
    </svg>
  );
};

// ── 5. Rocket Launch Detailed ────────────────────────────────────────
export const RocketLaunchDetailed: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const altitude = Math.min(phase * 0.5, 1);
  const rocketY = 350 - altitude * 250;
  return (
    <svg viewBox="0 0 600 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Sky gradient */}
      <defs>
        <linearGradient id="rld-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0f172a" />
          <stop offset="60%" stopColor="#1e3a8a" />
          <stop offset="100%" stopColor="#60a5fa" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="600" height="500" fill="url(#rld-sky)" />
      {/* Stars */}
      {[60, 120, 200, 350, 450, 530, 80, 500].map((sx, i) => (
        <circle key={i} cx={sx} cy={30 + (i * 37) % 200} r="1.5" fill="#fff" opacity={0.4 + Math.sin(phase * 0.3 + i) * 0.3} />
      ))}
      {/* Launch pad */}
      <rect x="230" y="400" width="140" height="15" rx="3" fill="#475569" />
      <rect x="255" y="415" width="10" height="30" fill="#6b7280" />
      <rect x="335" y="415" width="10" height="30" fill="#6b7280" />
      {/* Rocket */}
      <g transform={`translate(0,${-altitude * 250})`}>
        {/* Body */}
        <rect x="275" y="280" width="50" height="120" rx="4" fill="#e2e8f0" stroke="#cbd5e1" strokeWidth="2" />
        {/* Nose cone */}
        <polygon points="275,280 300,230 325,280" fill="#ef4444" />
        {/* Window */}
        <circle cx="300" cy="310" r="12" fill="#1e3a8a" stroke="#cbd5e1" strokeWidth="2" />
        <circle cx="300" cy="310" r="7" fill="#3b82f6" />
        {/* Fins */}
        <polygon points="275,380 255,410 275,400" fill="#ef4444" />
        <polygon points="325,380 345,410 325,400" fill="#ef4444" />
        {/* Flame */}
        {altitude < 1 && (
          <g>
            <ellipse cx="300" cy="415" rx="15" ry="25" fill="#f59e0b" opacity="0.8">
              <animate attributeName="ry" values="20;30;20" dur="0.2s" repeatCount="indefinite" />
            </ellipse>
            <ellipse cx="300" cy="420" rx="8" ry="18" fill="#ef4444">
              <animate attributeName="ry" values="15;22;15" dur="0.15s" repeatCount="indefinite" />
            </ellipse>
          </g>
        )}
      </g>
      {/* Smoke clouds at base */}
      {altitude > 0 && altitude < 0.5 && (
        <g>
          <ellipse cx="260" cy="420" rx="30" ry="15" fill="#94a3b8" opacity={0.5 - altitude}>
            <animate attributeName="rx" values="20;50;80" dur="3s" />
          </ellipse>
          <ellipse cx="340" cy="425" rx="25" ry="12" fill="#94a3b8" opacity={0.4 - altitude * 0.5}>
            <animate attributeName="rx" values="15;40;70" dur="3s" />
          </ellipse>
        </g>
      )}
    </svg>
  );
};

// ── 6. Spaceship Warp ────────────────────────────────────────────────
export const SpaceshipWarp: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const warpStretch = 1 + Math.sin(phase * 0.5) * 0.3;
  return (
    <svg viewBox="0 0 800 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      <rect x="0" y="0" width="800" height="400" fill="#0f172a" />
      {/* Warp stars */}
      {Array.from({ length: 30 }).map((_, i) => {
        const sx = 50 + (i * 97) % 700;
        const sy = 20 + (i * 53) % 360;
        return (
          <line key={i} x1={sx} y1={sy} x2={sx + 30 * warpStretch} y2={sy} stroke="#fff" strokeWidth="1" opacity="0.3">
            <animate attributeName="x1" values={`${sx};${sx - 80};${sx}`} dur={`${1 + (i % 5) * 0.3}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.1;0.4;0.1" dur={`${1 + (i % 5) * 0.3}s`} repeatCount="indefinite" />
          </line>
        );
      })}
      {/* Spaceship */}
      <g transform={`scale(${warpStretch},1)`} style={{ transformOrigin: "320px 200px" }}>
        <ellipse cx="400" cy="200" rx="80" ry="25" fill="#6366f1" />
        <ellipse cx="400" cy="195" rx="50" ry="30" fill="#818cf8" />
        <ellipse cx="400" cy="188" rx="20" ry="12" fill="#bfdbfe" opacity="0.7" />
        {/* Engine glow */}
        <ellipse cx="325" cy="200" rx="10" ry="5" fill="#22d3ee">
          <animate attributeName="rx" values="8;14;8" dur="0.3s" repeatCount="indefinite" />
        </ellipse>
      </g>
      <text x="400" y="370" textAnchor="middle" fill="#a5b4fc" fontSize="16" fontWeight="bold">Warp Speed</text>
    </svg>
  );
};

// ── 7. Yacht Sailing ─────────────────────────────────────────────────
export const YachtSailing: React.FC<Record<string, unknown>> = (props) => {
  const phase = (props.phase as number) ?? 0;
  const rock = Math.sin(phase * 0.8) * 4;
  return (
    <svg viewBox="0 0 800 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Sky */}
      <rect x="0" y="0" width="800" height="230" fill="#bfdbfe" />
      <circle cx="680" cy="70" r="35" fill="#fbbf24" />
      {/* Water */}
      <rect x="0" y="230" width="800" height="170" fill="#2563eb" />
      <path d="M0,240 Q100,225 200,240 Q300,255 400,240 Q500,225 600,240 Q700,255 800,240" fill="none" stroke="#60a5fa" strokeWidth="2" opacity="0.5">
        <animate attributeName="d" values="M0,240 Q100,225 200,240 Q300,255 400,240 Q500,225 600,240 Q700,255 800,240;M0,240 Q100,255 200,240 Q300,225 400,240 Q500,255 600,240 Q700,225 800,240;M0,240 Q100,225 200,240 Q300,255 400,240 Q500,225 600,240 Q700,255 800,240" dur="3s" repeatCount="indefinite" />
      </path>
      {/* Yacht - rocking */}
      <g transform={`rotate(${rock},400,240)`}>
        {/* Hull */}
        <path d="M300,250 Q310,290 400,290 Q490,290 500,250 Z" fill="#f8fafc" stroke="#94a3b8" strokeWidth="2" />
        {/* Mast */}
        <line x1="400" y1="250" x2="400" y2="110" stroke="#78350f" strokeWidth="4" />
        {/* Main sail */}
        <polygon points="405,120 405,240 500,240" fill="#fff" stroke="#cbd5e1" strokeWidth="1.5" />
        {/* Jib sail */}
        <polygon points="395,120 395,200 330,240" fill="#dbeafe" stroke="#cbd5e1" strokeWidth="1" />
        {/* Flag */}
        <polygon points="400,110 400,100 420,105" fill="#ef4444">
          <animate attributeName="points" values="400,110 400,100 420,105;400,110 400,100 425,107;400,110 400,100 420,105" dur="1s" repeatCount="indefinite" />
        </polygon>
        {/* Windows */}
        {[360, 400, 440].map((wx, i) => (
          <circle key={i} cx={wx} cy="265" r="5" fill="#3b82f6" />
        ))}
      </g>
      {/* Seagulls */}
      <path d="M150,80 Q160,70 170,80" fill="none" stroke="#475569" strokeWidth="1.5" />
      <path d="M180,60 Q190,50 200,60" fill="none" stroke="#475569" strokeWidth="1.5" />
    </svg>
  );
};

// ── 8. Bicycle Ride ──────────────────────────────────────────────────
export const BicycleRide: React.FC<Record<string, unknown>> = (props) => {
  const rotation = (props.rotation as number) ?? 0;
  const wheelSpin = rotation * 3;
  const pedalAngle = rotation * 2;
  return (
    <svg viewBox="0 0 800 400" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Ground */}
      <rect x="0" y="310" width="800" height="90" fill="#86efac" />
      <line x1="0" y1="310" x2="800" y2="310" stroke="#16a34a" strokeWidth="2" />
      {/* Background trees */}
      {[100, 300, 600, 720].map((tx, i) => (
        <g key={i}>
          <rect x={tx} y={260 - i * 5} width="8" height={50 + i * 5} fill="#78350f" />
          <circle cx={tx + 4} cy={250 - i * 5} r={15 + i * 3} fill="#16a34a" opacity="0.4" />
        </g>
      ))}
      {/* Bicycle frame */}
      <g>
        {/* Wheels */}
        <g transform={`rotate(${wheelSpin},320,280)`}>
          <circle cx="320" cy="280" r="35" fill="none" stroke="#475569" strokeWidth="3" />
          {[0, 60, 120, 180, 240, 300].map((a, i) => (
            <line key={i} x1="320" y1="280" x2={320 + 30 * Math.cos(a * Math.PI / 180)} y2={280 + 30 * Math.sin(a * Math.PI / 180)} stroke="#6b7280" strokeWidth="1" />
          ))}
          <circle cx="320" cy="280" r="5" fill="#6b7280" />
        </g>
        <g transform={`rotate(${wheelSpin},500,280)`}>
          <circle cx="500" cy="280" r="35" fill="none" stroke="#475569" strokeWidth="3" />
          {[0, 60, 120, 180, 240, 300].map((a, i) => (
            <line key={i} x1="500" y1="280" x2={500 + 30 * Math.cos(a * Math.PI / 180)} y2={280 + 30 * Math.sin(a * Math.PI / 180)} stroke="#6b7280" strokeWidth="1" />
          ))}
          <circle cx="500" cy="280" r="5" fill="#6b7280" />
        </g>
        {/* Frame */}
        <line x1="320" y1="280" x2="410" y2="230" stroke="#dc2626" strokeWidth="4" />
        <line x1="410" y1="230" x2="500" y2="280" stroke="#dc2626" strokeWidth="4" />
        <line x1="410" y1="230" x2="410" y2="280" stroke="#dc2626" strokeWidth="4" />
        <line x1="410" y1="280" x2="320" y2="280" stroke="#dc2626" strokeWidth="4" />
        {/* Seat */}
        <rect x="398" y="222" width="24" height="6" rx="3" fill="#1e293b" />
        {/* Handlebar */}
        <line x1="500" y1="280" x2="510" y2="240" stroke="#475569" strokeWidth="3" />
        <line x1="500" y1="245" x2="520" y2="248" stroke="#475569" strokeWidth="3" />
        {/* Pedals */}
        <circle cx="410" cy="280" r="8" fill="#6b7280" />
        <line x1={410 + 15 * Math.cos(pedalAngle * Math.PI / 180)} y1={280 + 15 * Math.sin(pedalAngle * Math.PI / 180)}
              x2={410 - 15 * Math.cos(pedalAngle * Math.PI / 180)} y2={280 - 15 * Math.sin(pedalAngle * Math.PI / 180)}
              stroke="#475569" strokeWidth="3" />
        {/* Rider */}
        <circle cx="415" cy="200" r="14" fill="#fbbf24" />
        <rect x="407" y="214" width="16" height="12" rx="3" fill="#6366f1" />
      </g>
    </svg>
  );
};
